import { useState } from "react";
import { backend } from "@/services/backend";
import { OwnerState, OwnerFiles, SaveResult } from "@/types/owner-edit";
import {
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
  OWNER_EDIT_CONSTANTS,
} from "@/constants/owner-edit";
import { enqueueSnackbar, closeSnackbar } from "notistack";

export const useOwnerSave = () => {
  const [isSaving, setIsSaving] = useState(false);

  const showSuccessMessage = (message: string) => {
    enqueueSnackbar(message, {
      SnackbarProps: {
        onClick: () => {
          closeSnackbar();
        },
      },
      variant: "success",
      autoHideDuration: OWNER_EDIT_CONSTANTS.NOTIFICATION_DURATION,
    });
  };

  const showErrorMessage = (message: string) => {
    enqueueSnackbar(message, {
      SnackbarProps: {
        onClick: () => {
          closeSnackbar();
        },
      },
      variant: "error",
      autoHideDuration: OWNER_EDIT_CONSTANTS.NOTIFICATION_DURATION,
    });
  };

  const saveUsername = async (username: string): Promise<boolean> => {
    try {
      const response = await backend.setUserDisplayName(username);
      if (response) {
        showSuccessMessage("Username updated");
        return true;
      } else {
        showErrorMessage("Username update failed");
        return false;
      }
    } catch (error) {
      showErrorMessage("Username update failed");
      return false;
    }
  };

  const saveAvatar = async (file: File, address: string): Promise<boolean> => {
    try {
      const response = await backend.uploadOwnerPhoto(file, address, "avatar");
      if (response) {
        showSuccessMessage("Avatar upload successful");
        return true;
      } else {
        showErrorMessage("Avatar upload failed");
        return false;
      }
    } catch (error) {
      showErrorMessage("Avatar upload failed");
      return false;
    }
  };

  const saveBackground = async (
    file: File,
    address: string
  ): Promise<boolean> => {
    try {
      const response = await backend.uploadOwnerPhoto(file, address, "cover");
      if (response) {
        showSuccessMessage("Background upload successful");
        return true;
      } else {
        showErrorMessage("Background upload failed");
        return false;
      }
    } catch (error) {
      console.error("Error uploading background:", error);
      showErrorMessage("Background upload failed");
      return false;
    }
  };

  const saveDescription = async (
    address: string,
    description: string
  ): Promise<boolean> => {
    try {
      const response = await backend.updateWalletDescription({
        address,
        text: description,
      });
      if (response) {
        showSuccessMessage("Description updated");
        return true;
      } else {
        showErrorMessage("Description update failed");
        return false;
      }
    } catch (error) {
      showErrorMessage("Description update failed");
      return false;
    }
  };

  const saveSocials = async (
    address: string,
    socials: any[]
  ): Promise<boolean> => {
    try {
      const response = await backend.setUserSocials({
        address,
        socials,
      });
      if (response) {
        showSuccessMessage("Socials updated");
        return true;
      } else {
        showErrorMessage("Socials update failed");
        return false;
      }
    } catch (error) {
      showErrorMessage("Socials update failed");
      return false;
    }
  };

  const deleteAvatar = async (avatarId: number): Promise<boolean> => {
    try {
      const response = await backend.deleteOwnerPhoto([avatarId]);
      if (response) {
        showSuccessMessage("Avatar deleted");
        return true;
      } else {
        showErrorMessage("Avatar delete failed");
        return false;
      }
    } catch (error) {
      showErrorMessage("Avatar delete failed");
      return false;
    }
  };

  const saveOwnerData = async (
    ownerData: OwnerState,
    originalOwnerData: OwnerState,
    files: OwnerFiles,
    address: string
  ): Promise<SaveResult> => {
    if (isSaving) {
      return {
        success: false,
        hasErrors: true,
        errors: ["Save already in progress"],
      };
    }

    setIsSaving(true);
    const errors: string[] = [];

    try {
      // Handle avatar deletion
      if (
        originalOwnerData.avatar !== OWNER_EDIT_CONSTANTS.DEFAULT_AVATAR &&
        ownerData.avatar === OWNER_EDIT_CONSTANTS.DEFAULT_AVATAR
      ) {
        if (originalOwnerData.avatarId > 0) {
          const success = await deleteAvatar(originalOwnerData.avatarId);
          if (!success) {
            errors.push("Avatar deletion failed");
          }
        }
      }

      // Handle username update
      if (originalOwnerData.username !== ownerData.username) {
        const success = await saveUsername(ownerData.username);
        if (!success) {
          errors.push("Username update failed");
        }
      }

      // Handle file uploads and NFT selections
      // Check for NFT selection first (even if no file, NFT selection takes precedence)
      if (files.avatarNftNumber) {
        const success = await backend.selectUserSkin(files.avatarNftNumber);
        if (success) {
          showSuccessMessage("Avatar upload successful");
        } else {
          showErrorMessage("Avatar upload failed");
          errors.push("Avatar upload failed");
        }
      } else if (files.avatar) {
        // Only upload file if no NFT was selected
        const success = await saveAvatar(files.avatar, address);
        if (success) {
          await backend.deSelectUserSkin("avatar");
        } else {
          errors.push("Avatar upload failed");
        }
      }

      if (files.backgroundNftNumber) {
        const success = await backend.selectUserSkin(files.backgroundNftNumber);
        if (success) {
          showSuccessMessage("Background upload successful");
        } else {
          showErrorMessage("Background upload failed");
          errors.push("Background upload failed");
        }
      } else if (files.background) {
        // Only upload file if no NFT was selected
        const success = await saveBackground(files.background, address);
        if (success) {
          await backend.deSelectUserSkin("background");
        } else {
          errors.push("Background upload failed");
        }
      }

      // Handle description update
      if (originalOwnerData.info !== ownerData.info) {
        const success = await saveDescription(address, ownerData.info);
        if (!success) {
          errors.push("Description update failed");
        }
      }

      // Handle socials update
      if (
        JSON.stringify(originalOwnerData.socials) !==
        JSON.stringify(ownerData.socials)
      ) {
        const success = await saveSocials(address, ownerData.socials);
        if (!success) {
          errors.push("Socials update failed");
        }
      }

      const hasErrors = errors.length > 0;
      return {
        success: !hasErrors,
        hasErrors,
        errors,
      };
    } catch (error) {
      showErrorMessage("Error saving data");
      return {
        success: false,
        hasErrors: true,
        errors: ["Unexpected error occurred"],
      };
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    saveOwnerData,
    saveUsername,
    saveAvatar,
    saveBackground,
    saveDescription,
    saveSocials,
    deleteAvatar,
  };
};
